import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { PageHeader, SectionCard, Loader, KpiCard } from "@/components/common";
import { formatINR } from "@/lib/format";
import { ArrowDownCircle, ArrowUpCircle, Scale, AlertTriangle } from "lucide-react";
import { BarChart, Bar, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function CashFlow() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/cashflow").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader label="Loading cash flow…" />;
  const s = data.summary;

  const combined = [...data.weekly.map((w) => ({ label: w.week, cash_in: w.cash_in, cash_out: w.cash_out, actual: true })),
                    ...data.forecast.map((f) => ({ label: f.week, cash_in: f.expected_in, cash_out: f.expected_out, actual: false }))];

  return (
    <div>
      <PageHeader title="Cash Flow" subtitle="Money in, money out, and what's coming next." />

      {data.warning && (
        <div className="mb-6 rounded-xl border border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/5 p-4 flex gap-3 animate-fade-up" data-testid="cashflow-warning">
          <AlertTriangle className="h-5 w-5 text-[hsl(var(--warning))] shrink-0" />
          <div><div className="font-semibold text-[hsl(var(--warning))]">Cash-flow watch</div><p className="text-sm text-foreground/80 mt-0.5">{data.warning}</p></div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="cf-in" index={0} label="Cash In" value={formatINR(s.cash_in, { compact: true })} icon={ArrowDownCircle} accent="success" />
        <KpiCard testid="cf-out" index={1} label="Cash Out" value={formatINR(s.cash_out, { compact: true })} icon={ArrowUpCircle} accent="danger" />
        <KpiCard testid="cf-receivable" index={2} label="Receivables" value={formatINR(s.receivables, { compact: true })} icon={ArrowDownCircle} accent="warning" />
        <KpiCard testid="cf-net" index={3} label="Net Position" value={formatINR(s.net_position, { compact: true })} icon={Scale} accent={s.net_position >= 0 ? "success" : "danger"} />
      </div>

      <SectionCard title="Weekly Cash Movement + Forecast" subtitle="Solid = actual · dashed = projected" className="mb-6" testid="cashflow-chart">
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={combined} margin={{ left: -18, right: 6 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.5} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} tickFormatter={(v) => formatINR(v, { compact: true })} />
            <Tooltip formatter={(v) => formatINR(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))" }} />
            <Legend />
            <Bar dataKey="cash_in" name="Cash In" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="cash_out" name="Cash Out" fill="#EF4444" radius={[4, 4, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </SectionCard>

      <SectionCard title="Expected Future Cash Flow" subtitle="Next 4 weeks — estimates only" testid="cashflow-forecast">
        <div className="grid sm:grid-cols-4 gap-4">
          {data.forecast.map((f, i) => (
            <div key={i} className="rounded-lg border border-border p-4 animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{f.week}</div>
              <div className="mt-2 font-heading text-xl font-bold tabular-nums">{formatINR(f.net, { compact: true })}</div>
              <div className="mt-1 text-xs text-muted-foreground">In {formatINR(f.expected_in, { compact: true })} · Out {formatINR(f.expected_out, { compact: true })}</div>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
